/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Users, TrendingUp, Receipt, AlertCircle, Package, Truck, DownloadCloud } from "lucide-react";
import SiteDashboardShell, { useSiteDashboard } from "@/components/customer/SiteDashboardShell";
import { downloadCustomerHandover, fetchSiteAppointments, fetchSiteCustomers, fetchSiteAnalytics, fetchSiteTaxCalendar, fetchCustomerWebsiteBilling, fetchSiteCommerceSummary, fetchCustomerHandover } from "@/lib/api";
import { isTagalogSelected } from "@/data/tagalogTranslations";

// The backend reports one of three customer-facing export states (handover_routes._export_state).
// Older responses without `export_state` are mapped from the raw lifecycle status so a deploy
// order mismatch can never show the wrong thing.
const EXPORT_STATE_VIEW = {
  preparing: { label: "Preparing Website Export", detail: "Your complete website package is being prepared. This page checks again automatically — you do not need to do anything." },
  ready: { label: "Ready for Download", detail: "Your website is yours. MySiteCare does not host it under this purchase — host it wherever you choose. You can download it as many times as you need." },
  failed: { label: "Export Failed", detail: "We could not prepare your website package. MySiteCare has been notified; contact info@mysitecare.eu if you need it sooner." },
};
const STATUS_TO_EXPORT_STATE = { download_available: "ready", downloaded: "ready", validation_failed: "failed" };
const HANDOVER_POLL_MS = 30000;

function exportStateOf(handover) {
  return handover.export_state || STATUS_TO_EXPORT_STATE[handover.status] || "preparing";
}

function AttentionCard({ icon: Icon, title, items, cta, to, empty }) {
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm" data-testid={`overview-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-[#0F172A] font-semibold"><Icon size={17} className="text-[#10B981]" /> {title}</div>
      {items.length === 0 ? (
        // key forces a fresh mount whenever the placeholder text itself changes
        // (e.g. from a "loading"-era default like "Nothing due soon." to the real
        // fetched "Tax Calendar Setup Required.") rather than React diffing the
        // existing text node in place — the reviewed-Filipino text walker only
        // re-scans newly-mounted nodes, so an in-place text swap would otherwise
        // silently revert to English once real data arrives. See LanguageSwitcher.js.
        <p key={`empty:${empty}`} className="mt-2 text-sm text-[#94A3B8]">{empty}</p>
      ) : (
        <ul className="mt-2 space-y-1 text-sm text-[#475569]">
          {items.map((line, i) => <li key={i}>{line}</li>)}
        </ul>
      )}
      {to && <Link to={to} className="mt-3 inline-block text-sm font-medium text-[#10B981] hover:underline">{cta}</Link>}
    </div>
  );
}

export default function SiteOverview() {
  const dashboard = useSiteDashboard();
  const { token, slug, capabilities } = dashboard;
  const [appointments, setAppointments] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [taxCalendar, setTaxCalendar] = useState(null);
  const [billing, setBilling] = useState(null);
  const [commerce, setCommerce] = useState(null);
  const [handover, setHandover] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    let state = "preparing";
    const load = () => fetchCustomerHandover(token, slug).then((data) => {
      if (cancelled) return;
      state = data.ownership_purchased ? exportStateOf(data) : "none";
      setHandover(data);
    }).catch(() => {});
    load();
    // While the export is still being prepared, keep checking so "Download My Website"
    // appears on its own once the worker has released the package.
    const timer = setInterval(() => {
      if (state === "preparing" && document.visibilityState !== "hidden") load();
    }, HANDOVER_POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [token, slug]);

  const downloadWebsite = async () => {
    setDownloading(true);
    setDownloadError("");
    try {
      await downloadCustomerHandover(token, handover.download_url, slug);
    } catch (err) {
      setDownloadError(err?.response?.status === 410
        ? "This download link expired. Reload the page to get a fresh one."
        : "The download did not complete. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!token || !capabilities) return;
    if (capabilities.appointments) fetchSiteAppointments(token, slug, { status: "pending", limit: 5 }).then(setAppointments).catch(() => {});
    if (capabilities.customers) fetchSiteCustomers(token, slug, { status: "new_lead", limit: 5 }).then(setCustomers).catch(() => {});
    if (capabilities.analytics) fetchSiteAnalytics(token, slug, "30d").then(setAnalytics).catch(() => {});
    if (capabilities.tax_calendar) fetchSiteTaxCalendar(token, slug).then(setTaxCalendar).catch(() => {});
    if (capabilities.commerce || capabilities.orders) fetchSiteCommerceSummary(token, slug).then(setCommerce).catch(() => {});
    fetchCustomerWebsiteBilling(token, slug).then(setBilling).catch(() => {});
  }, [token, slug, capabilities]);

  const today = new Date().toISOString().slice(0, 10);
  const todaysAppointments = (appointments?.items || []).filter((a) => a.requested_date === today);
  // Numbers interpolated into a stat label ("3 visitors") can never be matched by the
  // reviewed-Filipino text walker (it only matches a whole, exact, static string) —
  // these need the label word itself translated inline instead. Gated on the
  // dashboard's own language choice, not on region (this route has no /ph prefix).
  const isTL = isTagalogSelected();

  return (
    <SiteDashboardShell active="overview" dashboard={dashboard}>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#10B981] mb-4">What needs your attention today</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {capabilities?.appointments && (
          <AttentionCard
            icon={CalendarClock} title="Appointment Requests"
            items={(appointments?.items || []).slice(0, 4).map((a) => `${a.customer_name} — ${a.requested_date}${a.requested_time ? ` ${a.requested_time}` : ""}`)}
            empty="No pending appointment requests." cta="View agenda" to={`/account/websites/${slug}/agenda`}
          />
        )}
        {capabilities?.customers && (
          <AttentionCard
            icon={Users} title="New Leads"
            items={(customers?.items || []).slice(0, 4).map((c) => c.name)}
            empty="No new leads yet." cta="View customers" to={`/account/websites/${slug}/customers`}
          />
        )}
        {capabilities?.analytics && analytics && (
          <AttentionCard
            icon={TrendingUp} title="Website Performance (30 days)"
            items={isTL
              ? [`${analytics.pageviews} bisita`, `${analytics.form_submissions} katanungan`, `${analytics.confirmed_appointments} nakumpirmang appointment`]
              : [`${analytics.pageviews} visitors`, `${analytics.form_submissions} inquiries`, `${analytics.confirmed_appointments} confirmed appointments`]}
            empty="No visitor data recorded yet." cta="View analytics" to={`/account/websites/${slug}/analytics`}
          />
        )}
        {commerce && (
          <>
            <AttentionCard
              icon={Package} title="Orders Today"
              items={(isTL
                ? [`${commerce.orders_today} order`, commerce.revenue_today_minor > 0 ? `₱${(commerce.revenue_today_minor / 100).toLocaleString("en-PH")} na kita` : null, `${commerce.orders_awaiting_processing} hinihintay iproseso`]
                : [`${commerce.orders_today} order${commerce.orders_today === 1 ? "" : "s"}`, commerce.revenue_today_minor > 0 ? `₱${(commerce.revenue_today_minor / 100).toLocaleString("en-PH")} revenue` : null, `${commerce.orders_awaiting_processing} awaiting processing`]
              ).filter(Boolean)}
              empty="No orders yet." cta="View orders" to={`/account/websites/${slug}/orders`}
            />
            {capabilities?.shipping && (
              <AttentionCard icon={Truck} title="Ready to Ship"
                items={commerce.orders_ready_to_ship > 0 ? [isTL ? `${commerce.orders_ready_to_ship} order na handa na` : `${commerce.orders_ready_to_ship} order${commerce.orders_ready_to_ship === 1 ? "" : "s"} ready`] : []}
                empty="Nothing ready to ship." cta="View shipping" to={`/account/websites/${slug}/shipping`} />
            )}
            {commerce.inventory && (
              <AttentionCard icon={Package} title="Inventory"
                items={(isTL
                  ? [commerce.inventory.low_stock_count > 0 ? `${commerce.inventory.low_stock_count} produktong mababa na ang stock` : null, commerce.inventory.out_of_stock_count > 0 ? `${commerce.inventory.out_of_stock_count} ubos na ang stock` : null]
                  : [commerce.inventory.low_stock_count > 0 ? `${commerce.inventory.low_stock_count} products low in stock` : null, commerce.inventory.out_of_stock_count > 0 ? `${commerce.inventory.out_of_stock_count} out of stock` : null]
                ).filter(Boolean)}
                empty="Stock levels look fine." cta="View inventory" to={`/account/websites/${slug}/inventory`} />
            )}
          </>
        )}
        {capabilities?.tax_calendar && (
          <AttentionCard
            icon={AlertCircle} title="Tax & Compliance"
            items={taxCalendar?.setup_required ? [] : (taxCalendar?.next_deadlines || []).slice(0, 3).map((d) => `${d.due_date} — ${d.title}`)}
            empty={taxCalendar?.setup_required ? "Tax Calendar Setup Required." : "Nothing due soon."}
            cta="View tax calendar" to={`/account/websites/${slug}/tax-calendar`}
          />
        )}
        <AttentionCard
          icon={Receipt} title="MySiteCare"
          items={billing?.subscription ? (isTL
            ? [`Status ng plano: ${billing.subscription.status}`, billing.subscription.next_billing_at ? `Susunod na bayad: ${new Date(billing.subscription.next_billing_at).toLocaleDateString()}` : null]
            : [`Plan status: ${billing.subscription.status}`, billing.subscription.next_billing_at ? `Next payment: ${new Date(billing.subscription.next_billing_at).toLocaleDateString()}` : null]
          ).filter(Boolean) : []}
          empty="No active subscription on file." cta="View billing" to={`/account/websites/${slug}/billing`}
        />
        {handover?.ownership_purchased && exportStateOf(handover) !== "not_applicable" && (() => {
          const exportState = exportStateOf(handover);
          const view = EXPORT_STATE_VIEW[exportState] || EXPORT_STATE_VIEW.preparing;
          return (
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="overview-card-website-handover" data-export-state={exportState}>
              <div className="flex items-center gap-2 text-[#0F172A] font-semibold"><DownloadCloud size={17} className="text-[#10B981]" /> Website Handover</div>
              <p className="mt-2 text-sm font-medium text-[#0F172A]" data-testid="website-handover-status">{view.label}</p>
              <p className="mt-1 text-sm text-[#475569]">{view.detail}</p>
              {exportState === "ready" && handover.download_url && (
                <button type="button" onClick={downloadWebsite} disabled={downloading} className="mt-3 inline-block rounded-lg bg-[#10B981] px-4 py-2 text-sm font-medium text-white hover:bg-[#0EA371] disabled:opacity-60" data-testid="website-handover-download-button">
                  {downloading ? "Downloading…" : "Download My Website"}
                </button>
              )}
              {downloadError && <p role="alert" className="mt-2 text-sm text-red-600" data-testid="website-handover-download-error">{downloadError}</p>}
            </div>
          );
        })()}
      </div>

      {capabilities?.appointments && todaysAppointments.length > 0 && (
        <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-5">
          <p className="font-semibold text-[#0F172A] mb-2">Today</p>
          <ul className="text-sm text-[#475569] space-y-1">
            {todaysAppointments.map((a) => <li key={a.id}>{a.requested_time || "—"} — {a.customer_name}{a.service ? ` (${a.service})` : ""}</li>)}
          </ul>
        </div>
      )}
    </SiteDashboardShell>
  );
}
